import React, {useMemo, useState, useCallback, useEffect} from "react";
import {debounce} from "lodash";
import {useStore} from "@vigo/hooks";
import {AutoComplete} from "antd";
import {MaskedInput} from "antd-mask-input";

const normalizePhoneNumber = rawPhone => {
    if (!rawPhone) return "";
    let digits = rawPhone.toString().replace(/[^0-9]/g, "");

    if (digits.startsWith("0090")) {
        digits = digits.slice(4);
    } else if (digits.startsWith("90") && digits.length > 11) {
        digits = digits.slice(2);
    }

    if (digits.length === 10) {
        digits = `0${digits}`;
    } else if (digits.length > 11) {
        digits = digits.slice(-11);
    }

    if (digits.length !== 11) {
        return "";
    }

    return digits;
};

const CustomerSearchList = ({
    value,
    onChange,
    selectedSubCompanyId,
    fieldType = "text",
    placeholder,
    disabled = false,
    name,
    mask,
    size = "middle",
    onCustomerSelect,
    ...props
}) => {
    const stores = useStore();
    const [searchText, setSearchText] = useState("");
    const [inputValue, setInputValue] = useState(value || "");
    const [data, setData] = useState([]);
    const [previousResults, setPreviousResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isUserTyping, setIsUserTyping] = useState(false);
    const debounceTimeout = 800;

    const fetchCustomers = useCallback(
        debounce(async (searchValue, subCompanyId) => {
            if (!searchValue || searchValue.length < 3 || !subCompanyId) return;

            setLoading(true);
            try {
                const result = await stores.subCompanyStore.searchCustomers(
                    searchValue,
                    subCompanyId
                );
                setData(result);
                setPreviousResults(result);
                setIsUserTyping(false);
            } catch (error) {
                setData([]);
            } finally {
                setLoading(false);
            }
        }, debounceTimeout),
        []
    );

    useEffect(() => {
        if (searchText && searchText.length >= 3 && selectedSubCompanyId) {
            fetchCustomers(searchText, selectedSubCompanyId);
        } else {
            setData([]);
            setPreviousResults([]);
            setLoading(false);
        }
    }, [searchText, selectedSubCompanyId, fetchCustomers]);

    const onSearchUser = useMemo(() => {
        const loadOptions = value => {
            setSearchText(value);
            setIsUserTyping(true);
        };
        return debounce(loadOptions, debounceTimeout);
    }, [debounceTimeout]);

    useEffect(() => {
        if (!isUserTyping) {
            setInputValue(value || "");
        }
    }, [value, isUserTyping]);

    useEffect(() => {
        if (!searchText || searchText.length < 3) {
            setPreviousResults([]);
            setIsUserTyping(false);
        }
    }, [searchText]);

    const handleSearch = searchValue => {
        let actualValue = searchValue;

        if (fieldType === "text") {
            actualValue = actualValue.replace(/[^a-zA-ZğüşıöçĞÜŞİÖÇ\s]/g, "");
        } else if (fieldType === "alphanumeric") {
            actualValue = actualValue.replace(/[^a-zA-Z0-9ğüşıöçĞÜŞİÖÇ\s]/g, "");
        }

        setInputValue(actualValue);
        setIsUserTyping(true);
        onSearchUser(actualValue);
    };

    const handleSelect = (selectedValue, option) => {
        const selectedCustomer = option.customer;

        if (selectedCustomer) {
            const normalizedPhone = normalizePhoneNumber(selectedCustomer.receiverPhone);
            const actualDisplayValue =
                fieldType === "phone"
                    ? normalizedPhone || selectedCustomer.receiverPhone || ""
                    : selectedCustomer.receiverName;

            setInputValue(actualDisplayValue);
            setIsUserTyping(false);

            if (onChange) {
                onChange({target: {value: actualDisplayValue, name}});
            }

            if (onCustomerSelect) {
                const customerToSend = {
                    ...selectedCustomer,
                    receiverPhone: normalizedPhone || selectedCustomer.receiverPhone || ""
                };
                onCustomerSelect(customerToSend);
            }
        }
    };

    const handleChange = value => {
        let actualValue = value?.target?.value || value || "";

        if (fieldType === "text") {
            actualValue = actualValue.replace(/[^a-zA-ZğüşıöçĞÜŞİÖÇ\s]/g, "");
        } else if (fieldType === "alphanumeric") {
            actualValue = actualValue.replace(/[^a-zA-Z0-9ğüşıöçĞÜŞİÖÇ\s]/g, "");
        }

        if (fieldType === "phone") {
            const cleanValue = actualValue.replace(/[-,(,),_\s]/g, "");
            onSearchUser(cleanValue);
        }

        setInputValue(actualValue);
        setIsUserTyping(true);
        if (onChange) {
            onChange({target: {value: actualValue, name}});
        }
    };

    const currentData =
        (loading || isUserTyping) && previousResults.length > 0 ? previousResults : data;

    const options =
        currentData?.map((customer, index) => {
            const normalizedPhone = normalizePhoneNumber(customer.receiverPhone);
            const primaryInfo =
                fieldType === "phone"
                    ? normalizedPhone || customer.receiverPhone
                    : customer.receiverName;
            const secondaryInfo =
                fieldType === "phone"
                    ? customer.receiverName
                    : normalizedPhone || customer.receiverPhone;

            const uniqueValue = `${primaryInfo}_${index}`;

            return {
                value: uniqueValue,
                customer: customer,
                label: (
                    <div>
                        <div style={{fontWeight: "bold"}}>{primaryInfo}</div>
                        <div style={{fontSize: "12px", color: "#666"}}>{secondaryInfo}</div>
                        <div style={{fontSize: "11px", color: "#999", marginTop: "2px"}}>
                            {customer.receiverAddress?.substring(0, 50)}
                            {customer.receiverAddress?.length > 50 ? "..." : ""}
                        </div>
                    </div>
                )
            };
        }) || [];

    if (fieldType === "phone") {
        return (
            <div style={{width: "100%", border: "none !important"}}>
                <AutoComplete
                    value={inputValue}
                    options={options}
                    onSelect={handleSelect}
                    style={{width: "100%", border: "none !important"}}
                    size={size}
                    className="customer-search-autocomplete"
                    notFoundContent={
                        !selectedSubCompanyId
                            ? "Önce alt firma seçiniz"
                            : searchText && searchText.length >= 3
                            ? "Müşteri bulunamadı"
                            : "En az 3 karakter giriniz"
                    }
                    loading={loading}
                    dropdownStyle={{zIndex: 9999}}
                    getPopupContainer={trigger => trigger.parentNode}
                >
                    <MaskedInput
                        placeholder={placeholder}
                        mask={mask}
                        name={name}
                        value={inputValue}
                        onChange={handleChange}
                        disabled={disabled || !selectedSubCompanyId}
                        style={{
                            width: "100%",
                            "&.ant-input": {
                                border: "none !important"
                            }
                        }}
                        size={size}
                        {...props}
                    />
                </AutoComplete>
            </div>
        );
    }

    return (
        <AutoComplete
            value={inputValue}
            options={options}
            onSearch={handleSearch}
            onSelect={handleSelect}
            onChange={handleChange}
            placeholder={placeholder}
            style={{width: "100%"}}
            size={size}
            className="customer-search-autocomplete"
            notFoundContent={
                !selectedSubCompanyId
                    ? "Önce alt firma seçiniz"
                    : searchText && searchText.length >= 3
                    ? "Müşteri bulunamadı"
                    : "En az 3 karakter giriniz"
            }
            loading={loading}
            dropdownStyle={{zIndex: 9999}}
            getPopupContainer={trigger => trigger.parentNode}
            {...props}
        />
    );
};

export default CustomerSearchList;
